<template>
  <div>
    <div class="tw-flex tw-justify-center tw-justify-items-center tw-my-8">
      <back-nav to="SelectOption" />
    </div>
    <v-card class="tw-pb-12 tw-px-4">
      <card-title icon="mdi-autorenew">Overview</card-title>
      <top-stepper current="PersonalDetails" :cover-type="this.coverType"/>
      <div class="tw-px-0 md:tw-px-10 xl:tw-px-16 tw-mt-4 md:tw-mt-10">
        <h3 class="c-blue-text c-title tw-text-base md:tw-text-lg tw-mt-3 md:tw-mt-10">Insured details</h3>
        <hr class="tw-mt-3 tw-mb-5"/>
        <v-form v-model="isValid">
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3">
            <v-text-field
              v-model="form.fullName"
              class="my-2 tw-px-0 md:tw-px-6"
              label="Full Name"
              type="text"
              :rules="[required('Full Name')]"
              :disabled="!!form.fullName"
            ></v-text-field>
            <v-text-field
              v-model="form.idNumber"
              class="my-2 tw-px-0 md:tw-px-6"
              label="ID No."
              type="number"
              :rules="[required('ID No.'), minLength('ID No.', 6), maxLength('IS NO.', 8)]"
              :disabled="!!renewalClient.id"
            ></v-text-field>
            <v-select
              :v-model="form.gender"
              label="Gender"
              class="my-2 tw-px-0 md:tw-px-6"
              :items="['Male','Female']"
              :rules="[required('Gender')]"
              :disabled="!!form.gender"
            ></v-select>
            <v-text-field
              v-model="form.mobileNumber"
              class="my-2 tw-px-0 md:tw-px-6"
              label="Mobile No."
              type="text"
              :disabled="!!form.mobileNumber"
            ></v-text-field>
            <v-text-field
              v-model="form.email"
              class="my-2 tw-px-0 md:tw-px-6"
              label="Email"
              type="text"
              :rules="[required('Email'), emailFormat()]"
              :disabled="!!form.email"
            ></v-text-field>
            <v-text-field
              v-model="form.poBoxNo"
              class="my-2 tw-px-0 md:tw-px-6"
              label="PO Box No."
              type="number"
              :rules="[required('PO Box No.')]"
            ></v-text-field>
            <v-autocomplete
              v-model="form.postalCode"
              :loading="postCodeLoading"
              :items="postCodeItems"
              :search-input.sync="postCodeSearch2"
              class="my-2 tw-px-0 md:tw-px-6"
              hide-no-data
              hide-details
              label="Postal Code"
              :rules="[required('Postal Code')]"
            ></v-autocomplete>
            <v-menu
                v-model="dobMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="form.dob"
                    class="my-2 tw-px-0 md:tw-px-6"
                    label="Date of Birth"
                    persistent-hint
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    :rules="[required('Date of Birth')]"
                ></v-text-field>
              </template>
              <v-date-picker
                  v-model="dob"
                  :max=" new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()).toISOString()"
                  no-title
                  @input="handleDobInput"
                  color="primary"
              ></v-date-picker>
            </v-menu>
            <v-text-field
              v-model="form.kraPinNumber"
              class="my-2 tw-px-0 md:tw-px-6"
              label="KRA PIN"
              type="text"
              :rules="[required('KRA PIN'), kraPinFormat()]"
              :disabled="!!form.kraPinNumber"
            ></v-text-field>

          </div>
          <h3 class="c-blue-text title-font tw-text-base md:tw-text-lg tw-mt-3 md:tw-mt-10">Declaration & Authorisation to obtain and use information</h3>
          <div class="tw-px-2 tw-py-5 c-gray-text">
            <span>
              I confirm that the information given is correct and accurate. Your policy will be sold to you based on the information you have provided. If the information provided is incorrect or incomplete, your policy could be canceled, or a claim rejected/not paid in full.
            </span>
            </div>
          <div class="tw-px-2 tw-py-5 c-gray-text">
            <v-checkbox
                v-model="form.understoodTerm"
                label="Terms and conditions of this product."
                :rules="[check()]"
            >
              <template #label>
                <terms-and-conditions pre="I also confirm that i’ve read and understood the " post="of this product." />
              </template>
            </v-checkbox>
            <v-checkbox
                v-model="form.authorizeTerm"
                :rules="[check()]"
            >
              <template #label>
              <span>
                I authorize APA Insurance to obtain and use my personal information.
                I also confirm that I’ve read and understood the Apollo Group’s Privacy Policy that can be accessed through this
                <a href="https://www.apainsurance.org/privacy_policy" target="_blank" @click.stop class="link-text" rel="noopener">APA privacy policy</a>.
              </span>
              </template>
            </v-checkbox>
          </div>
          <v-card-actions class="col-sm-4 offset-sm-4 tw-mt-5 tw-pb-8">
            <v-btn
              block
              large
              color="secondary"
              class="my-3"
              :loading="loading"
              :disabled="!isValid || loading"
              @click="continueRenew"
            >
              {{ isValid ? "Continue" : "Check if all fields are filled" }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </div>
    </v-card>
  </div>
</template>

<script>
import CardTitle from '@/components/shared/CardTitle';
import validations from '@/utils/validations';
import {
  changeFormat, diffDate, formatDate, formatDate2,
} from '@/utils/time';
import axios from 'axios';
import postalCodes from '@/utils/postalCodes.json';
import { mapGetters } from 'vuex';
import BackNav from '@/components/buy/BackNav';
import { FB_RENEW_REVIEW_PERSONAL_DETAILS } from '@/utils/const';
import moment from 'moment';
import renewalMixin from '@/mixins/renewalMixin';
import { log } from '@/utils/errorLogger';
import TopStepper from '@/components/buy/TopStepper';
import TermsAndConditions from '@/components/TermsAndConditions';
import getCustomerId from '@/utils/getCustomerId';

export default {
  name: 'PersonalDetailsReview',
  components: {
    TermsAndConditions, TopStepper, BackNav, CardTitle,
  },
  props: {
    changeCover: {
      default: null,
      type: Object,
    },
  },
  data: () => ({
    dob: null,
    dobMenu: false,
    loading: false,
    isValid: false,
    form: {
      dob: '',
      fullName: '',
      mobileNumber: '',
      email: '',
      idNumber: '',
      poBoxNo: '',
      postalCode: '',
      kraPinNumber: '',
      gender: '',
      coverFrom: '',
      understoodTerm: '',
      authorizeTerm: '',
    },
    vehicleDetails: {},
    pageLoading: true,
    ...validations,
    postCodeLoading: false,
    postCodeItems: [],
    postCodeSearch2: null,
    date: null,
    menu: false,
  }),
  mixins: [renewalMixin],
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'authenticatedUser']),
    computedDateFormatted() {
      return this.form.coverFrom || this.formatDate(this.date);
    },
    policy() {
      return this.$store.state.policy;
    },
    renewalClient() {
      return this.$store.getters['renewal/renewalClient'];
    },
  },
  watch: {
    postCodeSearch2(val) {
      if (val && val !== this.form.postalCode) {
        this.postCodeFilter2(val);
      }
    },
    isValid(newValue, oldValue) {
      this.loading = true;
      if (oldValue === false && newValue === true) {
        axios.post('/motor/api/policy/vehicles', {
          batchNo: this.$route.params.batchNo,
        }).then((response) => {
          if (response.data.code === 0) {
            this.vehicleDetails = response.data.data[0];
          } else {
            this.$toast.error(response.data.message, 'Renewal Error', {
              timeout: 15000,
            });
            this.$router.push({ name: 'MyPolicies' });
          }
          this.loading = false;
        });
      }
    },
  },
  metaInfo: {
    title: 'Renew - Review Personal Details',
  },
  mounted() {
    const cleanPostalAddress = this.renewalClient.postalAddress
      ? this.renewalClient.postalAddress.replace(/\D/g, '')
      : '';
    this.form = {
      fullName: this.policy.clientName || this.authenticatedUser.name,
      dob: this.renewalClient.dateOfBirth || '',
      mobileNumber: this.renewalClient.mobileNo || this.authenticatedUser.phone_number,
      email: this.renewalClient.email || this.authenticatedUser.email,
      idNumber: this.renewalClient.id,
      poBoxNo: cleanPostalAddress,
      postalCode: this.renewalClient.postalCode,
      kraPinNumber: this.renewalClient.pinNo || this.authenticatedUser['custom:kra-pin'],
      gender: this.renewalClient.gender,
      coverFrom: !diffDate(this.policy.renewalDate)
        ? this.formatDate(this.policy.renewalDate)
        : moment().format('DD/MM/YYYY'),
    };
    this.form.mobileNumber = this.form.mobileNumber.startsWith('07')
      ? `+254${this.form.mobileNumber.substring(1)}`
      : this.form.mobileNumber;
    this.postCodeItems = [this.renewalClient.postalCode];
  },
  methods: {
    async continueRenew() {
      this.loading = true;
      const personalData = {
        dob: this.form.dob,
        email: this.form.email,
        fullName: this.form.fullName,
        idNumber: this.form.idNumber,
        kraPinNumber: this.form.kraPinNumber,
        mobileNo: this.form.mobileNumber,
        gender: this.form.gender,
        coverFrom: this.computedDateFormatted,
        policyNo: this.policy.policyNo || '',
        premium: this.policy.premium || 0,
        productId: this.policy.productCode || '',
        sumInsured: this.policy.sumInsured || 0,
        customerId: getCustomerId(),
      };

      localStorage.setItem('personalData', JSON.stringify(personalData));

      try {
        this.$toast.success(
          'Personal Details confirmed successfully',
          'Personal Details',
        );
        this.loading = false;
        this.$analytics.logEvent(FB_RENEW_REVIEW_PERSONAL_DETAILS, {
          product: 'Renewal Journey Review Personal Details',
        });
        this.$emit('to-vehicle-details-review', {
          data: this.vehicleDetails,
          switchedCover: false,
          personalData,
        });
      } catch (error) {
        log(error, this, 'continueRenew');
        this.loading = false;
        this.$toast.error('An error occurred. Please try again.', 'Error', {
          timeout: 15000,
        });
      }
    },
    formatDate,
    formatDate2,
    changeFormat,
    postCodeFilter2(v) {
      this.postCodeLoading = true;
      setTimeout(() => {
        this.postCodeItems = postalCodes.filter(
          (e) => (e || '').toUpperCase().indexOf((v || '').toUpperCase()) > -1,
        );
        this.postCodeLoading = false;
      }, 500);
    },
    handleDobInput(date) {
      this.dobMenu = false;
      this.form.dob = this.formatDate(date);
    },
  },
};
</script>
